import Select from "../Inputs/Select";
import Input from "../Inputs/Input";
import IncomeYearlyIncrease from "./IncomeYearlyIncrease";
import {
  BasicAnnuity as BasicAnnuityType,
  Person,
  ScenarioSettings,
} from "src/types";
import { useState } from "react";
import Button from "../Inputs/Button";
import StartEndSelect from "./StartEndSelect";
import AnnuitySchedule from "./AnnuitySchedule";

interface Props {
  annuity: BasicAnnuityType;
  people: Person[];
  setIncome: (income: BasicAnnuityType) => void;
  settings: ScenarioSettings;
}

const BasicAnnuity = ({
  people,
  annuity: pension,
  setIncome,
  settings,
}: Props) => {
  const options = [...people] as any[];
  if (people.length == 2) {
    options.push({ name: "Joint", id: -1 });
  }
  const amount = pension.amount
    ? pension.amount
    : {
        type: "yearly",
        value: pension.annualAmount,
      };

  const [openYears, setOpenYears] = useState(false);

  return (
    <>
      <div className="grow">
        <div className="flex flex-col gap-4">
          {people.length > 1 && (
            <Select
              options={options}
              selected={
                pension.personId == -1
                  ? { name: "Joint", id: -1 }
                  : people[pension.personId]
              }
              width="w-64!"
              setSelected={(i) => {
                let newIncome = {
                  ...pension,
                  personId: i.id,
                };
                if (
                  ["age", "death", "retirement"].includes(pension.start.type) && i.id !== -1
                ) {
                  newIncome.start = { ...pension.start, personId: i.id } as any;
                }
                setIncome(newIncome);
              }}
              label="Person"
            />
          )}
          <Input
            label="Title"
            subtype="text"
            width="w-64!"
            size="income"
            value={pension.name}
            setValue={(name) => setIncome({ ...pension, name })}
          />
          <div className="flex items-center gap-1">
            <div className="text-sm text-[#344054] min-w-36">
              Initial Amount
            </div>
            <Select
              options={[
                { name: "Manual", id: "Manual" },
                { name: "Schedule", id: "Schedule" },
              ]}
              selected={{
                name: pension.amountType,
                id: pension.amountType,
              }}
              width="w-32!"
              setSelected={(i) => setIncome({ ...pension, amountType: i.id })}
              label=""
            />
            {pension.amountType === "Schedule" ? (
              <Button type="primary" onClick={() => setOpenYears(true)}>
                Edit schedule
              </Button>
            ) : (
              <Input
                label=""
                subtype="mo/yr"
                size="income"
                width="w-36!"
                value={amount}
                setValue={(name) => setIncome({ ...pension, amount: name })}
              />
            )}
          </div>

          <StartEndSelect
            label="Start"
            shownOptions={[
              ...(pension.personId != -1
                ? [["death", pension.personId]]
                : (people.map((p) => ["death", p.id] as any) as any)),
              ...(pension.personId != -1
                ? [["age", pension.personId]]
                : (people.map((p) => ["age", p.id] as any) as any)),
              ["calendar-year"],
              ["#-of-years"],
              ...(pension.personId != -1
                ? [["retirement", pension.personId]]
                : (people.map(
                    (p) => ["retirement", p.id] as any,
                  ) as any)),
            ]}
            value={pension.start}
            setValue={(start) => setIncome({ ...pension, start })}
            settings={settings}
          />

          {people.length > 1 && pension.personId != -1 && (
            <Input
              label="Survivor %"
              subtype="percent"
              width="w-64!"
              size="income"
              tooltip="What percentage of the annuity will the survivor receive?"
              value={pension.survivorPercent}
              setValue={(name) =>
                setIncome({ ...pension, survivorPercent: name })
              }
            />
          )}
          <IncomeYearlyIncrease
            labels={false}
            increase={
              pension.yearlyIncrease || {
                type: "custom",
                percent: pension.yearlyIncreasePercent,
              }
            }
            setYearlyIncrease={(yearlyIncrease) =>
              setIncome({ ...pension, yearlyIncrease })
            }
          />
          <Input
            label="First-Year Prorate"
            subtype="percent"
            size="income"
            width="w-64!"
            value={pension.firstYearProRatePercent}
            tooltip="What percentage of the annuity will be paid in the first year?"
            setValue={(name) =>
              setIncome({
                ...pension,
                firstYearProRatePercent: parseInt(name),
              })
            }
          />
          <Select
            options={[
              { name: "Taxable", id: "Taxable" },
              { name: "Tax-Deferred", id: "Tax-Deferred" },
              { name: "Tax-Free", id: "Tax-Free" },
            ]}
            selected={{
              name: pension.taxType,
              id: pension.taxType,
            }}
            width="w-64!"
            setSelected={(i) => setIncome({ ...pension, taxType: i.id })}
            label="Tax Status"
          />
        </div>
      </div>

      <AnnuitySchedule openYears={openYears} setOpenYears={setOpenYears} settings={settings} people={people} pension={pension} setIncome={setIncome}/>
    </>
  );
};

export default BasicAnnuity;
